import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * E2E auth bypass.
 *
 * The Playwright suite mints its own RS256 token (see
 * `apps/app/e2e/support/e2e-jwt.ts`) and injects it into `localStorage` before
 * the app boots. When one is present, it becomes the Convex token and the route
 * guards report signed-in — no Clerk round-trip, so authenticated specs stop
 * hammering the rate-limited Clerk dev instance and can run in parallel.
 *
 * With no token the behaviour is byte-for-byte the normal Clerk path, so the
 * sign-in / sign-up specs still exercise real Clerk against the same dev server.
 *
 * `VITE_E2E_AUTH_BYPASS` is set ONLY by `apps/app/playwright.config.ts`, never
 * in `.env.local` or the Docker build args — so this whole branch is constant-
 * folded away in production bundles. Convex accepts these tokens only where
 * `E2E_AUTH_ENABLED` is set on the deployment (never production).
 */
const E2E_AUTH_BYPASS = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

const TOKEN_KEY = '__billAlpsE2eToken';

/**
 * Read once at module scope: Playwright's init script runs before any app code,
 * so the token is already there and never changes for the life of the page.
 * A constant also keeps the hooks below referentially stable.
 */
const E2E_TOKEN: string | null = (() => {
  if (!E2E_AUTH_BYPASS) return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

/**
 * Auth state for the route guards: the e2e token wins, otherwise Clerk decides.
 * Drop-in replacement for Clerk's `useAuth()` where only `isLoaded`/`isSignedIn`
 * are needed.
 */
export function useAppAuth(): { isLoaded: boolean; isSignedIn: boolean } {
  const { isLoaded, isSignedIn } = useAuth();
  if (E2E_TOKEN) return { isLoaded: true, isSignedIn: true };
  return { isLoaded, isSignedIn: !!isSignedIn };
}

/**
 * Token source for `ConvexProviderWithAuth`. Mirrors what
 * `ConvexProviderWithClerk` does (`getToken({ template: 'convex' })`), with the
 * e2e token short-circuiting it.
 *
 * Must stay a module-scope function: `ConvexProviderWithAuth` resets auth state
 * to loading whenever the `useAuth` prop identity changes.
 */
export function useConvexAuthFromClerk(): {
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchAccessToken: (args: {
    forceRefreshToken: boolean;
  }) => Promise<string | null>;
} {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      if (E2E_TOKEN) return E2E_TOKEN;
      try {
        return await getToken({
          template: 'convex',
          skipCache: forceRefreshToken,
        });
      } catch {
        return null;
      }
    },
    [getToken],
  );

  if (E2E_TOKEN) {
    return { isLoading: false, isAuthenticated: true, fetchAccessToken };
  }
  return {
    isLoading: !isLoaded,
    isAuthenticated: !!isSignedIn,
    fetchAccessToken,
  };
}
